import React, { useCallback } from 'react';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { useLanguage, useExportSchedule, useUser } from '~/Hooks';
import { useEventGroup, useEventGroupActions } from './hooks';
import { useEventGroupFilter } from './hooks/useEventGroupFilter';
import { EventProductTour, FitoListUncontrolled } from '~/Components';
import { EventGroup } from '~/Models/DTO/EventGroup.model';
import { Filter, FilterGroups } from '~/Components/Filter';
import { EndEventGroupConfirmationModal } from '~/Screens/EventGroupList/EndEventGroupConfirmationModal';
import { DeleteEventGroupConfirmationModal } from '~/Screens/EventGroupList/DeleteEventGroupConfirmationModal';
import { Button } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';

const useStyles = makeStyles((them) => ({
  filterWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: them.spacing(2),
  },
  button: {
    marginRight: '8px',
  },
}));

export const EventGroupList = () => {
  const { t } = useLanguage();
  const { startExport, status } = useExportSchedule();
  const { classes } = useStyles();
  const { DANGEROUSLY_isSuperAdmin: isSuperAdmin } = useUser();
  const downloadLink = () => {
    if (!!status?.downloadLink) {
      window.open(status.downloadLink, '_blank');
    }
  };
  const {
    open,
    groups,
    onOpen,
    onClose,
    onClear,
    onApply,
    selectedCount,
    appliedFilters,
  } = useEventGroupFilter();

  const {
    tabs,
    keyOfId,
    settings,
    selectedTab,
    placeholder,
    listActions,
    sortOptions,
    setItemValues,
    tableDefinition,
    createEventGroup,
    listReduxSelectorString,
  } = useEventGroup();

  const {
    getItemActions,
    endEventGroupOptions,
    deleteEventGroupOptions,
    onConfirmEndEventGroup,
    onCloseEndEventGroupModal,
    onConfirmDeleteEventGroup,
    onCloseDeleteEventGroupModal,
    onClick,
  } = useEventGroupActions({ selectedTab });

  const setItemValuesWithOnClick = useCallback((item: EventGroup) => setItemValues(item, onClick), [
    onClick,
  ]);

  return (
    <PrivateLayout
      title={{
        title: t('app.event.eventGroups'),
        onAddButtonClick: createEventGroup,
        actionName: t('event.createNew'),
      }}
      hideBackButton
    >
      <FitoListUncontrolled<EventGroup>
        listActions={listActions}
        keyOfId={keyOfId}
        settings={settings}
        filterComponent={
          <div className={classes.filterWrapper}>
            {isSuperAdmin && (
              <Button
                onClick={() => startExport()}
                variant="contained"
                size="small"
                className={classes.button}
                disabled={status?.status === 'InProgress'}
              >
                {t('app.export.schedule.btn')}
              </Button>
            )}
            {!!status?.downloadLink && (
              <Button onClick={downloadLink} variant="text" size="small" style={{ marginRight: 8 }}>
                {t('app.download')}
              </Button>
            )}
            <Filter
              open={open}
              onOpen={onOpen}
              onClose={onClose}
              onClear={onClear}
              onApply={onApply}
              selectedCount={selectedCount}
            >
              <FilterGroups groups={groups} />
            </Filter>
          </div>
        }
        getItemActions={getItemActions}
        placeholder={placeholder}
        appliedFilters={appliedFilters}
        title={t('settings.locations')}
        setItemValues={setItemValuesWithOnClick}
        tabs={tabs}
        listReduxSelectorString={listReduxSelectorString}
        tableDefinition={tableDefinition}
        sortOptions={sortOptions}
      />
      {endEventGroupOptions && (
        <EndEventGroupConfirmationModal
          onClose={onCloseEndEventGroupModal}
          onConfirm={onConfirmEndEventGroup}
          confirmationOptions={endEventGroupOptions}
        />
      )}
      {deleteEventGroupOptions && (
        <DeleteEventGroupConfirmationModal
          onClose={onCloseDeleteEventGroupModal}
          onConfirm={onConfirmDeleteEventGroup}
          confirmationOptions={deleteEventGroupOptions}
        />
      )}
      <EventProductTour />
    </PrivateLayout>
  );
};
